import {Component, OnInit} from '@angular/core';
import {RiskProfileRange, RiskProfileRequest, RiskProfileResponse, RiskQuestion} from '../../../shared/types/risk-profile.type';
import {InvestmentAllocation} from '../../../shared/types/pension-calculator.type';
import {GENERAL_CONSTANTS, INVESTMENT_TYPES, RISK_PROFILE_RANGE} from '../../../shared/constants/constant';
import {LangChangeEvent, TranslateService} from '@ngx-translate/core';
import {RiskProfileService} from './risk-profile.service';
import {switchMap} from 'rxjs/operators';
import {TokenService} from '../../../shared/services/token.service';

@Component({
  selector: 'app-risk-profile',
  templateUrl: './risk-profile.component.html',
  styleUrls: ['./risk-profile.component.css']
})
export class RiskProfileComponent implements OnInit {
  public showScore: boolean = false;
  public showQuestionaire: boolean = false;
  public numberOfQuestions: number = 7;
  public selectedAnswer: number[] = Array(this.numberOfQuestions).fill(null);
  public totalValue: number = 0;

  public error: boolean = false;
  public success: boolean = false;
  public errorMessage: string = GENERAL_CONSTANTS.EMPTY_STRING;
  public browserLang: string;
  public lang: string;
  public account: string;

  public investmentAllocation: InvestmentAllocation[] = INVESTMENT_TYPES;
  public riskProfileQuestions: RiskQuestion[];
  public riskProfileResult: RiskProfileRange;
  public riskProfile: RiskProfileResponse;
  public riskProfileRequest: RiskProfileRequest;

  constructor(
    public riskProfileService: RiskProfileService,
    private translate: TranslateService,
    private tokenService: TokenService
  ) {
    this.lang = translate.currentLang;

    this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      this.browserLang = event.lang;
      this.lang = this.browserLang;
    });
  }

  ngOnInit() {
    this.account = this.tokenService.secureGet("account").authorities[0];
    this.showQuestionaire = true;
    this.investmentAllocation = INVESTMENT_TYPES;
    this.riskProfileQuestions = this.generateQuestions(this.numberOfQuestions);
  }

  public generateQuestions(numQuestions: number): RiskQuestion[] {
    const questions: RiskQuestion[] = [];

    for (let i = 1; i <= numQuestions; i++) {
      const question = {
        question: `q${i}`,
        options: [
          { label: `optionsQ${i}.label1`, value: 1 },
          { label: `optionsQ${i}.label2`, value: 2 },
          { label: `optionsQ${i}.label3`, value: 3 }
        ]
      };
      questions.push(question);
    }

    return questions;
  }

  public selectAnswer(questionIndex: number, value: number): void {
    if (this.selectedAnswer[questionIndex] !== undefined) {
        this.totalValue -= this.selectedAnswer[questionIndex];
    }

    this.selectedAnswer[questionIndex] = value;
    this.totalValue += value;

    this.riskProfileResult = this.findProfileAndDefinition();
  }


  public findProfileAndDefinition(): RiskProfileRange | null {
    for (const range of RISK_PROFILE_RANGE) {
      if (this.totalValue >= range.min && this.totalValue <= range.max) {
        return {
          riskProfile: range.riskProfile,
          definition: range.definition,
          investOption: range.investOption
        };
      }
    }
    return null;
  }

  public resetQuestionaire() {
    this.clearError();

    this.showQuestionaire = true;
    this.showScore = false;
    this.totalValue = 0;
    this.selectedAnswer = Array(this.numberOfQuestions).fill(null);
  }

  public scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  public translatePayload(data: any): void {
    const riskProfileDef = data.calculator.pensionCalculatorQuestionaire.riskProfile;

    const translatedPayload = {
      riskProfile: riskProfileDef[this.riskProfileResult.riskProfile.split(GENERAL_CONSTANTS.DOT).pop() || GENERAL_CONSTANTS.EMPTY_STRING],
      definition: riskProfileDef[this.riskProfileResult.definition.split(GENERAL_CONSTANTS.DOT).pop() || GENERAL_CONSTANTS.EMPTY_STRING],
      investOption: riskProfileDef[this.riskProfileResult.investOption.split(GENERAL_CONSTANTS.DOT).pop() || GENERAL_CONSTANTS.EMPTY_STRING]
    };

    const accountDetails = this.tokenService.secureGet("accountDetails");
    this.riskProfileRequest = {
      userId: accountDetails.id,
      questions: this.selectedAnswer,
      total: this.totalValue,
      result: translatedPayload
    }
  }

  public constructPayload(): void {
    const token = this.tokenService.secureGet("token");
    this.riskProfileService.loadLocale(this.lang).pipe(
      switchMap(localeData => {
        this.translatePayload(localeData);
        return this.riskProfileService.submitRiskProfile(token, this.riskProfileRequest);
      })
    ).subscribe(
      data => {
         this.riskProfileResponse(data);
      },
      error => {
        console.log("error", error);
        this.showError("Server Error");
      }
    );
  }

  public retryBtn() {
    this.clearError();
    this.resetQuestionaire();
  }

  public nextBtn() {
    this.clearError();
    this.showQuestionaire = true;
    this.showScore = false;

    if (!this.validateTotalAnswers()) {
      this.showError('Please complete all the questionnaire.');
      this.scrollToTop();
      return;
    }

    this.constructPayload();
  }

  public riskProfileResponse(data): void {
    this.showQuestionaire = false;
    this.showScore = true;

    // TODO no need, maybe get the status to show success alert
    // if (data.replyMessageList && data.replyMessageList.length > 0) {
    //   const response = data.replyMessageList[0];

    //   this.riskProfile = {
    //     score: response.score,
    //     risk: response.risk,
    //     definition: response.definition,
    //     type: response.type,
    //   }
    // }
  }

  public validateTotalAnswers(): boolean {
    return this.selectedAnswer.length === this.numberOfQuestions &&
           this.selectedAnswer.every(answer => answer !== undefined && answer !== null);
  }

  public showError(message: string): void {
    this.error = true;
    this.errorMessage = message;
  }

  public clearError(): void {
    this.errorMessage = GENERAL_CONSTANTS.EMPTY_STRING;
    this.error = false;
  }
}
